// @LTG-ID: lib-cta-ids
// @STATUS: BUILT MOCKED-PATHS
// @HERMETIC: true
// @DOCS: 11_OPEN_QUESTIONS.md#OQ-001, 04b_SHARED_CONTRACT.md, 10_DECISIONS.md#ADR-007
// @CHANGED: 2026-02 session_002
// @OWNER: web
//
// Central registry of every data-cta-id used on the marketing site.
// Every entry is intent-revealing and MOCKED-PATHS until the CRM team confirms real
// app.letstalkgrowth.com routing. Update OQ-001 and this file together.
//
// Usage:
//   import { CTAS, LTG_APP_BASE, ctaHref } from "@/lib/cta-ids";
//   <ExternalCTA ctaId="hero-primary">…</ExternalCTA>
//
// Anti-pattern (will fail tester grep):
//   <a href="https://app.letstalkgrowth.com/...">…</a>

// CHG-0106 · APP_KGOB_BASE constant retired (target host never materialized;
// see ADR-032 for supersession rationale). LTG_APP_BASE below is the
// canonical outbound base for every registered "order" CTA — see ctaHref()
// at the bottom of this file.

// CHG-0099 — Primary CTA destination switched from KGOB CRM to the LTG sibling
// dashboard at app.letstalkgrowth.com/begin. Per ADR-031 (Producer/Consumer
// routing), the dashboard is the new onboarding surface for v1; KGOB receives
// leads via the deferred backend-side dual-write (ADR-027 §7 / CHG-0084).
// The `via=...` attribution query string from each CTA's `path` is preserved
// so funnel analytics in GA4 / the dashboard can attribute by surface.
export const LTG_APP_BASE = "https://app.letstalkgrowth.com";
export const BEGIN_PATH = "/begin";

// Every CTA the marketing site renders. Keys are the data-cta-id values.
export const CTAS = {
    "header-login": {
        ctaId: "header-login",
        path: "/client/login",
        intent: "Sibling dashboard client/admin login (auth-gated). Routes directly to LTG_APP_BASE + /client/login (NOT through /begin) — see ctaHref() prefix dispatch.",
        surface: "layout > Header",
        phase: "CHG-0099/Amendment-2",
    },
    "header-order": {
        path: "/order/snapshot?via=header",
        intent: "Open the Strategic Snapshot intake/checkout from the header",
        surface: "global > Header",
        phase: "P1",
    },
    "hero-primary": {
        path: "/order/snapshot?via=hero",
        intent: "Open the Snapshot intake from the Hero CTA",
        surface: "landing > Hero",
        phase: "P1",
    },
    "hero-secondary": {
        path: null, // local anchor only, no outbound href
        intent: "Scroll to the in-page Snapshot calculator (Snapshot section lands in P2)",
        surface: "landing > Hero",
        phase: "P1",
    },
    "page-nav-order": {
        path: "/order/snapshot?via=page-nav",
        intent: "Open the Snapshot intake from the sticky in-page nav",
        surface: "landing > PageNav",
        phase: "P1",
    },
    "archetype-result-lifestyle": {
        path: "/order/snapshot?via=archetype&archetype=lifestyle",
        intent: "Order from the Lifestyle Operator result card",
        surface: "landing > OwnerArchetype",
        phase: "P1",
    },
    "archetype-result-legacy": {
        path: "/order/snapshot?via=archetype&archetype=legacy",
        intent: "Order from the Legacy Builder result card",
        surface: "landing > OwnerArchetype",
        phase: "P1",
    },
    "archetype-result-liquidity": {
        path: "/order/snapshot?via=archetype&archetype=liquidity",
        intent: "Order from the Liquidity-Bound Owner result card",
        surface: "landing > OwnerArchetype",
        phase: "P1",
    },
    "archetype-result-stuck": {
        path: "/order/snapshot?via=archetype&archetype=stuck",
        intent: "Order from the Stuck Owner result card",
        surface: "landing > OwnerArchetype",
        phase: "P1",
    },

    // ----- Reserved for P2+ (do not consume yet) ------------------------------
    "snapshot-order": {
        path: "/order/snapshot?via=calculator",
        intent: "Order from inside the Snapshot calculator block",
        surface: "landing > Snapshot",
        phase: "P2",
    },
    "engagements-snapshot-order": {
        path: "/order/snapshot?via=engagements",
        intent: "Order the Snapshot from the engagement ladder",
        surface: "landing > Engagements",
        phase: "P2",
    },
    "cta-strip-order": {
        path: "/order/snapshot?via=cta-strip",
        intent: "Order from the final close strip",
        surface: "landing > CtaStrip",
        phase: "P2",
    },
    "ask-widget-order": {
        path: "/order/snapshot?via=ask",
        intent: "Order from the floating Q&A widget",
        surface: "landing > AskWidget",
        phase: "P2",
    },
    "diagnostic-result-order": {
        path: "/order/snapshot?via=diagnostic",
        intent: "Order after the exit-readiness ring",
        surface: "landing > Diagnostic",
        phase: "P2",
    },
    "levers-order": {
        path: "/order/snapshot?via=levers",
        intent: "Order from the 10-lever scorecard close",
        surface: "landing > Levers",
        phase: "P2",
    },
    "engagements-membership-locked": {
        path: "/order/membership?from=engagements",
        intent: "Locked Membership CTA in the Engagements ladder; server gates per 04b_SHARED_CONTRACT.md",
        surface: "landing > Engagements (gated card)",
        phase: "P2",
    },
    "engagements-monthly": {
        // CHG-0107.4 · NOTE-1 fix. Was a bare <a href="https://app.letstalkgrowth.com/begin">
        // in Engagements.jsx:146 with unregistered data-cta-id. Now routed through
        // <ExternalCTA> for UTM decoration + attribution snapshot merging (ADR-008).
        path: "/order/monthly?via=engagements-monthly",
        intent: "View monthly Growth Membership plans; routes to /begin funnel with attribution",
        surface: "landing > Engagements (monthly-plans link on Membership card)",
        phase: "CHG-0107.4",
    },
    "team-snapshot-order": {
        path: "/order/snapshot?via=team",
        intent: "Order CTA on Sara's principal profile",
        surface: "landing > Team",
        phase: "P2",
    },
    "refund-policy-order": {
        // PRE-RESERVED: source `/app/source_reference/Refund Policy.html` has no
        // in-page "Order Snapshot" CTA — Refund Policy is a legal document, not a
        // marketing surface. This cta-id is reserved for potential P6 addition
        // (e.g., a footer-level "Still ready? Order Snapshot →" anchor) without
        // requiring a new cta-id registration at that point. NOT WIRED today.
        path: "/order/snapshot?via=refund-policy",
        intent: "Pre-reserved: future in-page CTA on the Refund Policy page (P6)",
        surface: "route /refund-policy (pre-reserved, no live anchor)",
        phase: "P3",
    },

    // ----- P3-M2 Brief 01 (CHG-0060) ------------------------------------------
    "brief-01-order": {
        // Source `Journals/what-is-my-business-worth.html` has 3 outbound
        // `app.letstalkgrowth.com` anchors: (a) header chrome (covered by global
        // header-order), (b) one inline mid-article CTA (lines 1170-1173), and
        // (c) the end-of-article primary button (line 1251). The two in-body
        // anchors share the same destination/intent, so a single cta-id covers
        // both surfaces. Header chrome stays on `header-order`.
        path: "/order/snapshot?via=brief-01",
        intent:
            "Order the Strategic Snapshot from Brief 01 (inline + end-of-article)",
        surface: "route /journals/what-is-my-business-worth (Brief 01 body)",
        phase: "P3",
    },

    // ----- P3-M3 Brief 02 (CHG-0062) ------------------------------------------
    "brief-02-order": {
        // Source `Journals/sde-vs-ebitda.html` has 2 in-body outbound anchors:
        // (a) inline mid-article (lines 385-388), (b) end-of-article primary
        // (line 446). Header chrome stays on `header-order`. Same one-cta-id
        // pattern as `brief-01-order` to keep GTM event surface clean.
        path: "/order/snapshot?via=brief-02",
        intent:
            "Order the Strategic Snapshot from Brief 02 (inline + end-of-article)",
        surface: "route /journals/sde-vs-ebitda (Brief 02 body)",
        phase: "P3",
    },

    // ----- P3-M4 Journals index (CHG-0063) ------------------------------------
    "brief-locked-preview": {
        // Umbrella CTA for all 35 unpublished brief accordions on the Journals
        // index. The per-brief identity is preserved via the `brief=NN` query
        // param appended by ExternalCTA's `extraParams` prop, so GTM still gets
        // brief-level attribution without 35 individual cta-ids. ORCH-0050 A7
        // explicitly authorized this single-cta-id-with-param pattern.
        path: "/order/snapshot?via=brief-locked",
        intent:
            "Order Snapshot to unlock the full text of an editorial-state brief (preview state)",
        surface:
            "route /journals — inside expanded body of each unpublished accordion",
        phase: "P3",
    },
    // NOTE-2 (CHG-0107.4): `journals-anchor-order` deleted here — orphaned
    // registration with no live consumer after CHG-0104 replaced the 7-section
    // anchor accordion with a magazine-style flat index. Companion delete in
    // `data/journals-index.js` removed the `JOURNALS_ANCHOR_CTA` export.

    // ----- CHG-0102 Journal MVP (20 new articles) -----------------------
    "journal-article-primary": {
        // Umbrella CTA for the 20 CHG-0102 articles (Brief 38..57). The
        // end-of-article "Begin your growth journey →" primary anchor uses
        // this single cta-id; per-article attribution is preserved via the
        // article slug being visible in the referring URL (GA4 pageview) —
        // no `via=slug` here to keep the query surface stable across 20 pages.
        path: "/order/snapshot?via=journal-article",
        intent:
            "Order the Strategic Snapshot from the end-of-article CTA of any CHG-0102 Journal MVP article",
        surface: "route /journals/<slug> — 20 CHG-0102 articles (Brief 38..57)",
        phase: "P4",
    },
};

// Build the absolute href for a cta-id. Returns null for in-page anchor CTAs.
//
// CHG-0099 / ADR-031: destination is `https://app.letstalkgrowth.com/begin`
// for every registered outbound "order" CTA. The `via=...` (and any extra) query
// params from each CTA's `path` are preserved as attribution. The legacy KGOB
// destination has been retired from the user-facing funnel; KGOB receives leads
// via the deferred backend-side dual-write (ADR-027 §7 / CHG-0084), not via the
// browser.
//
// CHG-0099 Amendment 2: CTAs whose `path` begins with `/client/` or `/admin/`
// (auth-gated dashboard surfaces such as Log in) route DIRECTLY to
// `LTG_APP_BASE + path` without the /begin rewrite — those are dashboard-native
// destinations, not begin-funnel entrypoints.
export function ctaHref(ctaId) {
    const cta = CTAS[ctaId];
    if (!cta || !cta.path) return null;
    // Dashboard-native destinations (login, admin) — route as-is.
    if (cta.path.startsWith("/client/") || cta.path.startsWith("/admin/")) {
        return LTG_APP_BASE + cta.path;
    }
    // Begin-funnel CTAs — rewrite to /begin while preserving attribution.
    // e.g. "/order/snapshot?via=hero" → "/begin?via=hero".
    const qIndex = cta.path.indexOf("?");
    const search = qIndex >= 0 ? cta.path.slice(qIndex) : "";
    return LTG_APP_BASE + BEGIN_PATH + search;
}
